import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/_atoms/button";

interface BackupCodesDisplayProps {
  backupCodes: string[];
  onDownload?: () => void;
  onContinue?: () => void;
}

export function BackupCodesDisplay({ backupCodes, onDownload, onContinue }: BackupCodesDisplayProps) {
  const [isCopied, setIsCopied] = useState(false);
  const { t } = useTranslation("two-factor-auth");

  const handleCopyAll = async () => {
    const text = backupCodes.join("\n");
    try {
      await navigator.clipboard.writeText(text);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
      setIsCopied(false);
    }
  };

  const handleDownload = () => {
    const text = backupCodes.join("\n");
    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "backup-codes.txt";
    document.body.appendChild(a);
    try {
      a.click();
      if (onDownload) {
        onDownload();
      }
    } finally {
      if (document.body.contains(a)) {
        document.body.removeChild(a);
      }
      URL.revokeObjectURL(url);
    }
  };

  return (
    <div className="flex flex-col space-y-4">
      <div>
        <h3 className="mb-2 text-lg font-semibold">{t("backupCodes.title")}</h3>
        <p className="mb-4 text-sm text-muted-foreground">
          {t("backupCodes.description")}
        </p>
        <div className="mb-4 rounded-md border border-warning/20 bg-warning/10 p-4">
          <p className="text-sm text-warning-foreground">
            <strong>{t("backupCodes.important")}</strong> {t("backupCodes.warning")}
          </p>
        </div>
      </div>

      <div className="rounded-md bg-muted p-4">
        <div className="grid grid-cols-2 gap-2 font-mono text-sm">
          {backupCodes.map((code, index) => (
            <div key={index} className="rounded-sm border bg-background p-2 text-center">
              {code}
            </div>
          ))}
        </div>
      </div>

      <div className="flex space-x-2">
        <Button type="button" variant="outline" onClick={handleCopyAll} className="flex-1">
          {isCopied ? t("backupCodes.copied") : t("backupCodes.copyAll")}
        </Button>
        <Button type="button" variant="outline" onClick={handleDownload} className="flex-1">
          {t("backupCodes.download")}
        </Button>
      </div>

      {onContinue && (
        <Button type="button" onClick={onContinue} className="w-full">
          {t("backupCodes.savedConfirmation")}
        </Button>
      )}
    </div>
  );
}
